import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Bot, User, Sparkles, Info, Utensils, Dumbbell, Calendar, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Coach360Page() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchInitialMessage();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchInitialMessage = async () => {
    try {
      const response = await axios.get(`${API}/coach360/today`);
      setMessages([
        {
          role: 'assistant',
          content: response.data.message,
          timestamp: response.data.timestamp,
          context: response.data.dts_context,
          action: response.data.suggested_action
        }
      ]);
    } catch (error) {
      console.error('Failed to fetch initial message:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleQuickAction = async (actionType) => {
    setLoading(true);
    try {
      let response;
      let userMessage;
      
      if (actionType === 'meal') {
        userMessage = "What should I eat for my next meal?";
        response = await axios.get(`${API}/coach360/meal-suggestion?meal_type=lunch`);
      } else if (actionType === 'workout') {
        userMessage = "Suggest a workout for me";
        response = await axios.get(`${API}/coach360/workout-suggestion`);
      } else if (actionType === 'plan') {
        navigate('/daily-plan');
        return;
      }
      
      setMessages(prev => [
        ...prev,
        { role: 'user', content: userMessage, timestamp: new Date().toISOString() },
        { 
          role: 'assistant', 
          content: response.data.content,
          timestamp: new Date().toISOString(),
          suggestion: actionType === 'meal' ? response.data.meal : response.data.workout,
          suggestionType: actionType
        }
      ]);
    } catch (error) {
      toast.error('Failed to get suggestion');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/coach360/chat`, { message: input.trim() });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.content,
        timestamp: response.data.timestamp
      }]);
    } catch (error) {
      toast.error('Failed to send message');
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-secondary">
              <Bot size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold">Coach360</h1>
              <p className="text-xs text-muted-foreground">Your AI Wellness Coach</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <ProfileAvatar size="sm" />
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {initialLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-pulse text-muted-foreground">Loading conversation...</div>
          </div>
        ) : (
          messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[85%] ${message.role === 'user' ? 'order-2' : ''}`}>
                {message.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-lg bg-primary/10">
                      <Bot size={14} className="text-primary" />
                    </div>
                    <span className="text-xs text-muted-foreground">Coach360</span>
                  </div>
                )}
                <Card
                  className={`px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-primary text-white rounded-2xl rounded-br-md'
                      : 'glass-card rounded-2xl rounded-bl-md'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{message.content}</p>
                  {message.action && (
                    <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 text-xs">
                      <Sparkles size={12} />
                      <span>{message.action}</span>
                    </div>
                  )}
                  {/* Show meal/workout suggestion card */}
                  {message.suggestion && (
                    <a
                      href={
                        message.suggestionType === 'meal'
                          ? (message.suggestion.allrecipes_url || `https://www.allrecipes.com/search?q=${encodeURIComponent(message.suggestion.title)}`)
                          : (message.suggestion.justfit_url || `https://justfit.app/videos?search=${encodeURIComponent(message.suggestion.title)}`)
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-3 p-3 rounded-xl bg-primary/5 border border-primary/20 hover:border-primary/40 hover:bg-primary/10 transition-colors cursor-pointer"
                      data-testid={`suggestion-link-${message.suggestionType}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {message.suggestionType === 'meal' ? (
                          <Utensils size={14} className="text-primary" />
                        ) : (
                          <Dumbbell size={14} className="text-primary" />
                        )}
                        <span className="text-xs font-medium flex-1">{message.suggestion.title}</span>
                        <ExternalLink size={12} className="text-muted-foreground" />
                      </div>
                      <p className="text-xs text-muted-foreground">{message.suggestion.description}</p>
                      {message.suggestion.duration_minutes && (
                        <p className="text-xs text-primary mt-1">{message.suggestion.duration_minutes} min &middot; {message.suggestion.intensity}</p>
                      )}
                      {message.suggestion.calories_estimate && (
                        <p className="text-xs text-primary mt-1">~{message.suggestion.calories_estimate} cal &middot; {message.suggestion.prep_time}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1.5">
                        {message.suggestionType === 'meal' ? 'View on AllRecipes' : 'View on JustFit'} &rarr;
                      </p>
                    </a>
                  )}
                </Card>
                {message.context && (
                  <p className="text-xs text-muted-foreground mt-1 px-2">
                    Based on your DTS score of {message.context.score}
                  </p>
                )}
              </div>
            </motion.div>
          ))
        )}
        
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="glass-card px-4 py-3 rounded-2xl rounded-bl-md">
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </main>

      {/* Quick Actions */}
      <div className="px-4 py-2 border-t border-border bg-background/80 backdrop-blur-sm">
        <div className="flex gap-2 overflow-x-auto pb-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleQuickAction('meal')}
            disabled={loading}
            className="rounded-full whitespace-nowrap"
            data-testid="quick-meal-btn"
          >
            <Utensils size={14} className="mr-1" />
            Meal Idea
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleQuickAction('workout')}
            disabled={loading}
            className="rounded-full whitespace-nowrap"
            data-testid="quick-workout-btn"
          >
            <Dumbbell size={14} className="mr-1" />
            Workout
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleQuickAction('plan')}
            className="rounded-full whitespace-nowrap"
            data-testid="quick-plan-btn"
          >
            <Calendar size={14} className="mr-1" />
            Today&apos;s Plan
          </Button>
        </div>
      </div>

      {/* Input Area */}
      <div className="sticky bottom-20 bg-background/80 backdrop-blur-lg border-t border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask Coach360 anything..."
            className="flex-1 rounded-full h-12 px-4"
            disabled={loading}
            data-testid="coach-input"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            size="icon"
            className="rounded-full h-12 w-12 bg-primary hover:bg-primary/90"
            data-testid="coach-send-btn"
          >
            <Send size={18} />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground text-center mt-2">
          Coach360 provides wellness guidance, not medical advice
        </p>
      </div>

      <BottomNav />
    </div>
  );
}
